import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useNavigate } from 'react-router-dom';

import { Sidebar } from './components';
import Banner from './components/Banner/Banner';
import ErrorBoundary from './ErrorBoundary';
import Commissions from './pages/Commissions/Commissions';
import Credits from './pages/Credits/Credits';
import Customers from './pages/Customers/Customers';
import Dashboard from './pages/Dashboard';
import Drivers from './pages/Drivers/Drivers';
import DriverDetail from './pages/Drivers/ViewAndEditDetail/ViewAndEditDetail';
import Empties from './pages/Empties/Empties';
import EmptiesHistory from './pages/Empties/EmptiesHistory';
import ReturnEmpties from './pages/Empties/ReturnEmpties/CreateReturnEmpties';
import FinancialTransactions from './pages/FinancialTransaction/FinancialTransaction';
import CreateInvoice from './pages/Invoice/CreateInvoice/CreateInvoice';
import Invoices from './pages/Invoice/Invoices';
import Login from './pages/Login';
import NotFound from './pages/NotFound';
import CreateOrder from './pages/Orders/CreateOrder/CreateOrder';
import OrderDetail from './pages/Orders/OrderDetailPage/OrderDetails';
import Orders from './pages/Orders/Orders';
import Outlets from './pages/Outlets/Outlets';
import OutletDetail from './pages/Outlets/ViewAndEditDetail/ViewAndEditDetail';
import Products from './pages/Products/Products';
import Register from './pages/Register';
import Reports from './pages/Reports';
import CreateReturns from './pages/ReturnOrders/CreateReturns';
// import CreateReturns from './pages/ReturnOrders/CreateReturns';
import ReturnOrderDetails from './pages/ReturnOrders/ReturnOrderDetails/ReturnOrderDetails';
import ReturnOrders from './pages/ReturnOrders/ReturnOrders';
import UserSettings from './pages/Settings/Settings';
// import Sku from './pages/Sku';
import StockAdjustment from './pages/StockAdjustment/StockAdjustment';
import Unauthorized from './pages/Unauthorized';
import Users from './pages/Users/Users';
import UserDetail from './pages/Users/ViewAndEditDetail/ViewAndEditDetail';
import { landingPathFor, role } from './permissions';
import { RootState } from './store/redux/store';
import { logoutUser, noUser } from './store/redux/userRedux';
import { User } from './types';
import { authApi } from './utils/api';
import { TOKEN_KEY } from './utils/tenant';

interface RouteElement {
  children: JSX.Element;
  roles: string[];
}

export const PrivateRoute = ({ children, roles }: RouteElement) => {
  const user: User | null = useSelector((state: RootState) => state.user.currentUser);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else if (!roles.includes(user?.role)) {
      navigate('/unauthorized');
    }
  }, [user, roles, navigate]);

  return user ? (
    <>
      <Sidebar />
      {children}
    </>
  ) : null;
};

/**
 * "/" is reachable by every signed-in user — the sidebar brand links to it — so a
 * non-admin must be redirected rather than shown the unauthorized screen.
 */
const Home = () => {
  const user: User | null = useSelector((state: RootState) => state.user.currentUser);

  if (user?.role !== 'admin') {
    return <Navigate to={landingPathFor(user?.role)} replace />;
  }

  return <Dashboard />;
};

const App = () => {
  const dispatch = useDispatch();
  const token = localStorage.getItem(TOKEN_KEY);

  useEffect(() => {
    if (!token) {
      dispatch(noUser());
      return;
    }

    // Goes through the shared instance so the request carries the tenant key. Khufu does
    // not treat /auth as a public route, so a bare axios call would be rejected before it
    // ever reached the token check.
    const verify = async () => {
      try {
        await authApi({ method: 'GET', url: '/auth/verify-token' });
      } catch (error) {
        localStorage.removeItem(TOKEN_KEY);
        dispatch(logoutUser());
      }
    };

    verify();
  }, [token, dispatch]);

  const router = createBrowserRouter([
    {
      path: '/',
      element: (
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      ),
      children: [
        {
          path: '/unauthorized',
          element: <Unauthorized />
        },
        {
          path: '/',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <Home />
            </PrivateRoute>
          )
        },
        {
          path: '/register',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <Register />
            </PrivateRoute>
          )
        },
        {
          path: '/login',
          element: <Login />
        },
        {
          path: '/users/*',
          element: (
            <PrivateRoute roles={role.adminGroup}>
              <Users />
            </PrivateRoute>
          )
        },
        {
          path: '/user/:id',
          element: (
            <PrivateRoute roles={role.adminGroup}>
              <UserDetail />
            </PrivateRoute>
          )
        },
        {
          path: '/reports',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <Reports />
            </PrivateRoute>
          )
        },
        {
          path: '/empties/*',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <Empties />
            </PrivateRoute>
          )
        },
        {
          path: '/empties-history',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <EmptiesHistory />
            </PrivateRoute>
          )
        },
        {
          path: '/financial-transaction/*',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <FinancialTransactions />
            </PrivateRoute>
          )
        },
        {
          path: '/invoices/*',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <Invoices />
            </PrivateRoute>
          )
        },
        {
          path: '/orders/*',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <Orders />
            </PrivateRoute>
          )
        },
        {
          path: '/return-orders/*',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <ReturnOrders />
            </PrivateRoute>
          )
        },
        {
          path: '/stock-adjustment/*',
          element: (
            <PrivateRoute roles={role.stockGroup}>
              <StockAdjustment />
            </PrivateRoute>
          )
        },
        // {
        //   path: '/sku',
        //   element: (
        //     <PrivateRoute roles={role.generalGroup}>
        //       <Sku />
        //     </PrivateRoute>
        //   )
        // },
        {
          path: '/create-order',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <CreateOrder />
            </PrivateRoute>
          )
        },
        {
          path: '/return-empties',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <ReturnEmpties />
            </PrivateRoute>
          )
        },
        {
          path: '/products/*',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <Products />
            </PrivateRoute>
          )
        },
        {
          path: '/customers/*',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <Customers />
            </PrivateRoute>
          )
        },
        {
          path: '/credits/*',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <Credits />
            </PrivateRoute>
          )
        },
        {
          path: '/commissions/*',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <Commissions />
            </PrivateRoute>
          )
        },
        {
          path: '/create-invoice/:orderId',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <CreateInvoice />
            </PrivateRoute>
          )
        },
        {
          path: '/outlets/*',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <Outlets />
            </PrivateRoute>
          )
        },
        {
          path: '/settings/*',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <UserSettings />
            </PrivateRoute>
          )
        },
        {
          path: '/drivers/*',
          element: (
            <PrivateRoute roles={role.supervisorGroup}>
              <Drivers />
            </PrivateRoute>
          )
        },
        {
          path: '/outlet/:id',
          element: (
            <PrivateRoute roles={role.stockGroup}>
              <OutletDetail />
            </PrivateRoute>
          )
        },
        {
          path: '/driver/:id',
          element: (
            <PrivateRoute roles={role.supervisorGroup}>
              <DriverDetail />
            </PrivateRoute>
          )
        },
        {
          path: '/order/:id',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <OrderDetail />
            </PrivateRoute>
          )
        },
        {
          path: '/return-order/:id',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <ReturnOrderDetails />
            </PrivateRoute>
          )
        },
        {
          path: '/orders/:id/create-returns',
          element: (
            <PrivateRoute roles={role.generalGroup}>
              <CreateReturns />
            </PrivateRoute>
          )
        },
        {
          path: '*',
          element: <NotFound />
        }
      ]
    }
  ]);

  return (
    <>
      <Banner />
      <RouterProvider router={router} />;
    </>
  );
};

export default App;
